import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Type,
  Mic,
  Image,
  Link2,
  X,
  Search,
  Trash2,
  ChevronRight,
  Clock,
  Archive,
  Send,
  Check,
  Loader2
} from 'lucide-react';
import { Button, Card, Modal, Textarea, Input } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import { ideasApi, IdeaDto } from '../../services';
import './BrainDump.css';

// Local idea type for UI (maps to backend IdeaType)
type IdeaType = 'text' | 'voice' | 'image' | 'link';

// Map backend types to frontend types
const mapBackendType = (type: string): IdeaType => {
  const typeMap: Record<string, IdeaType> = {
    'Text': 'text',
    'Voice': 'voice',
    'Image': 'image',
    'Link': 'link'
  };
  return typeMap[type] || 'text';
};

// Map frontend types to backend types
const mapFrontendType = (type: IdeaType): string => {
  const typeMap: Record<IdeaType, string> = {
    'text': 'Text',
    'voice': 'Voice',
    'image': 'Image',
    'link': 'Link'
  };
  return typeMap[type];
};

interface Idea {
  id: string;
  type: IdeaType;
  content: string;
  tags: string[];
  createdAt: Date;
  imageUrl?: string;
  linkUrl?: string;
  linkTitle?: string;
  voiceDuration?: number;
}

// Transform backend DTO to frontend Idea
const transformIdea = (dto: IdeaDto): Idea => ({
  id: dto.id,
  type: mapBackendType(dto.type),
  content: dto.textContent || '',
  tags: dto.tags || [],
  createdAt: new Date(dto.createdAt),
  linkUrl: dto.contentUrl,
  linkTitle: dto.contentUrl ? 'Link' : undefined
});

// Available tags
const systemTags = [
  { id: 'song-idea', name: 'song idea', color: 'purple' },
  { id: 'lyric', name: 'lyric', color: 'pink' },
  { id: 'melody', name: 'melody', color: 'blue' },
  { id: 'sound', name: 'sound', color: 'teal' },
  { id: 'visual', name: 'visual', color: 'orange' },
  { id: 'concept', name: 'concept', color: 'green' },
  { id: 'reference', name: 'reference', color: 'gray' },
];

// Format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Idea type icon
const IdeaTypeIcon: React.FC<{ type: IdeaType; size?: number }> = ({ type, size = 18 }) => {
  switch (type) {
    case 'text':
      return <Type size={size} />;
    case 'voice':
      return <Mic size={size} />;
    case 'image':
      return <Image size={size} />;
    case 'link':
      return <Link2 size={size} />;
  }
};

export const BrainDump: React.FC = () => {
  useAuth();

  // Ideas state
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [filteredIdeas, setFilteredIdeas] = useState<Idea[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Capture modal state
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [captureType, setCaptureType] = useState<IdeaType>('text');
  const [captureContent, setCaptureContent] = useState('');
  const [captureUrl, setCaptureUrl] = useState('');
  const [captureTags, setCaptureTags] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);

  // FAB state
  const [isFabExpanded, setIsFabExpanded] = useState(false);

  // Detail modal
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);

  // Fetch ideas from API
  const fetchIdeas = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await ideasApi.getInbox();
      setIdeas(data.map(transformIdea));
    } catch (err) {
      console.error('Failed to fetch ideas:', err);
      setError('Failed to load ideas. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load ideas on mount
  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  // Filter ideas based on search and tags
  useEffect(() => {
    let filtered = [...ideas];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(idea =>
        idea.content.toLowerCase().includes(query) ||
        idea.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(idea =>
        selectedTags.some(tag => idea.tags.includes(tag))
      );
    }

    // Sort by most recent
    filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    setFilteredIdeas(filtered);
  }, [ideas, searchQuery, selectedTags]);

  const handleOpenCapture = (type: IdeaType) => {
    setCaptureType(type);
    setCaptureContent('');
    setCaptureUrl('');
    setCaptureTags([]);
    setShowCaptureModal(true);
    setIsFabExpanded(false);
  };

  const handleSaveIdea = async () => {
    if (!captureContent.trim() && captureType !== 'link') return;
    if (captureType === 'link' && !captureUrl.trim()) return;

    setIsCapturing(true);

    try {
      const newIdea = await ideasApi.create({
        type: mapFrontendType(captureType),
        textContent: captureContent.trim() || undefined,
        contentUrl: captureType === 'link' ? captureUrl : undefined,
        tags: captureTags.length > 0 ? captureTags : undefined
      });

      setIdeas(prev => [transformIdea(newIdea), ...prev]);
      setShowCaptureModal(false);
      setCaptureContent('');
      setCaptureUrl('');
      setCaptureTags([]);
    } catch (err) {
      console.error('Failed to save idea:', err);
      setError('Failed to save idea. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleDeleteIdea = async (id: string) => {
    try {
      await ideasApi.delete(id);
      setIdeas(prev => prev.filter(idea => idea.id !== id));
      setSelectedIdea(null);
    } catch (err) {
      console.error('Failed to delete idea:', err);
      setError('Failed to delete idea. Please try again.');
    }
  };

  const handleArchiveIdea = async (id: string) => {
    try {
      await ideasApi.archive(id);
      setIdeas(prev => prev.filter(idea => idea.id !== id));
      setSelectedIdea(null);
    } catch (err) {
      console.error('Failed to archive idea:', err);
      setError('Failed to archive idea. Please try again.');
    }
  };

  const toggleCaptureTag = (tagName: string) => {
    setCaptureTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const toggleFilterTag = (tagName: string) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  return (
    <AppLayout pageTitle="Brain Dump">
    <div className="brain-dump">
      {/* Ambient background */}
      <div className="brain-dump__ambient">
        <div className="brain-dump__ambient-shape brain-dump__ambient-shape--1" />
        <div className="brain-dump__ambient-shape brain-dump__ambient-shape--2" />
      </div>

      {/* Main Content */}
      <main className="brain-dump__main">
        {/* Intro */}
        <div className="brain-dump__intro">
          <p>Capture ideas fast. Organize later. No pressure.</p>
        </div>

        {/* Search and Filter */}
        <div className="brain-dump__filters">
          <div className="brain-dump__search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search ideas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>
                <X size={16} />
              </button>
            )}
          </div>

          <div className="brain-dump__tags-filter">
            {systemTags.map(tag => (
              <button
                key={tag.id}
                className={`brain-dump__tag-chip brain-dump__tag-chip--${tag.color} ${selectedTags.includes(tag.name) ? 'brain-dump__tag-chip--active' : ''}`}
                onClick={() => toggleFilterTag(tag.name)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ideas List */}
        <div className="brain-dump__list">
          {isLoading ? (
            <div className="brain-dump__loading">
              <Loader2 size={32} className="brain-dump__loading-spinner" />
              <p>Loading ideas...</p>
            </div>
          ) : error ? (
            <div className="brain-dump__error">
              <p>{error}</p>
              <Button onClick={fetchIdeas}>Try Again</Button>
            </div>
          ) : filteredIdeas.length > 0 ? (
            filteredIdeas.map((idea, index) => (
              <Card
                key={idea.id}
                variant="default"
                padding="md"
                className="brain-dump__idea-card"
                onClick={() => setSelectedIdea(idea)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="brain-dump__idea-header">
                  <span className="brain-dump__idea-type">
                    <IdeaTypeIcon type={idea.type} />
                  </span>
                  <span className="brain-dump__idea-time">
                    <Clock size={12} />
                    {formatRelativeTime(idea.createdAt)}
                  </span>
                </div>

                <p className="brain-dump__idea-content">{idea.content}</p>

                {idea.type === 'link' && idea.linkUrl && (
                  <div className="brain-dump__idea-link">
                    <Link2 size={14} />
                    <span>{idea.linkTitle || idea.linkUrl}</span>
                  </div>
                )}

                {idea.tags.length > 0 && (
                  <div className="brain-dump__idea-tags">
                    {idea.tags.map(tag => {
                      const tagInfo = systemTags.find(t => t.name === tag);
                      return (
                        <span
                          key={tag}
                          className={`brain-dump__idea-tag brain-dump__idea-tag--${tagInfo?.color || 'gray'}`}
                        >
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                )}
              </Card>
            ))
          ) : (
            <div className="brain-dump__empty">
              <span className="brain-dump__empty-icon">💭</span>
              <h3>No ideas yet</h3>
              <p>Tap the + button to capture your first idea</p>
            </div>
          )}
        </div>
      </main>

      {/* Floating Action Button */}
      {isFabExpanded && (
        <div className="brain-dump__fab-backdrop" onClick={() => setIsFabExpanded(false)} />
      )}
      <div className={`brain-dump__fab-container ${isFabExpanded ? 'brain-dump__fab-container--expanded' : ''}`}>
        {isFabExpanded && (
          <div className="brain-dump__fab-options">
            <button
              className="brain-dump__fab-option"
              onClick={() => handleOpenCapture('text')}
            >
              <Type size={20} />
              <span>Text</span>
            </button>
            <button
              className="brain-dump__fab-option"
              onClick={() => handleOpenCapture('voice')}
            >
              <Mic size={20} />
              <span>Voice</span>
            </button>
            <button
              className="brain-dump__fab-option"
              onClick={() => handleOpenCapture('image')}
            >
              <Image size={20} />
              <span>Image</span>
            </button>
            <button
              className="brain-dump__fab-option"
              onClick={() => handleOpenCapture('link')}
            >
              <Link2 size={20} />
              <span>Link</span>
            </button>
          </div>
        )}
        <button
          className={`brain-dump__fab ${isFabExpanded ? 'brain-dump__fab--expanded' : ''}`}
          onClick={() => setIsFabExpanded(!isFabExpanded)}
        >
          {isFabExpanded ? <X size={28} /> : <Plus size={28} />}
        </button>
      </div>

      {/* Capture Modal */}
      <Modal
        isOpen={showCaptureModal}
        onClose={() => setShowCaptureModal(false)}
        title={`Capture ${captureType.charAt(0).toUpperCase() + captureType.slice(1)} Idea`}
        size="md"
      >
        <div className="brain-dump__capture">
          {captureType === 'text' && (
            <Textarea
              placeholder="What's on your mind?"
              value={captureContent}
              onChange={(e) => setCaptureContent(e.target.value)}
              rows={4}
              autoFocus
            />
          )}

          {captureType === 'link' && (
            <>
              <Input
                placeholder="Paste URL here..."
                value={captureUrl}
                onChange={(e) => setCaptureUrl(e.target.value)}
                autoFocus
              />
              <Textarea
                placeholder="Add a note (optional)"
                value={captureContent}
                onChange={(e) => setCaptureContent(e.target.value)}
                rows={2}
              />
            </>
          )}

          {captureType === 'voice' && (
            <div className="brain-dump__capture-voice">
              <div className="brain-dump__capture-voice-icon">
                <Mic size={32} />
              </div>
              <p>Voice recording coming soon!</p>
              <span>For now, type your idea below:</span>
              <Textarea
                placeholder="Type what you wanted to say..."
                value={captureContent}
                onChange={(e) => setCaptureContent(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {captureType === 'image' && (
            <div className="brain-dump__capture-image">
              <div className="brain-dump__capture-image-placeholder">
                <Image size={32} />
                <p>Image capture coming soon!</p>
                <span>For now, add a description:</span>
              </div>
              <Textarea
                placeholder="Describe the image idea..."
                value={captureContent}
                onChange={(e) => setCaptureContent(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* Tag selector */}
          <div className="brain-dump__capture-tags">
            <label>Add tags (optional)</label>
            <div className="brain-dump__capture-tags-list">
              {systemTags.map(tag => (
                <button
                  key={tag.id}
                  className={`brain-dump__tag-chip brain-dump__tag-chip--${tag.color} ${captureTags.includes(tag.name) ? 'brain-dump__tag-chip--active' : ''}`}
                  onClick={() => toggleCaptureTag(tag.name)}
                >
                  {tag.name}
                  {captureTags.includes(tag.name) && <Check size={12} />}
                </button>
              ))}
            </div>
          </div>

          <div className="brain-dump__capture-actions">
            <Button variant="ghost" onClick={() => setShowCaptureModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveIdea}
              disabled={isCapturing || (!captureContent.trim() && captureType !== 'link') || (captureType === 'link' && !captureUrl.trim())}
            >
              {isCapturing ? 'Saving...' : 'Save Idea'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Idea Detail Modal */}
      <Modal
        isOpen={!!selectedIdea}
        onClose={() => setSelectedIdea(null)}
        size="md"
      >
        {selectedIdea && (
          <div className="brain-dump__detail">
            <div className="brain-dump__detail-header">
              <span className="brain-dump__detail-type">
                <IdeaTypeIcon type={selectedIdea.type} size={24} />
                {selectedIdea.type.charAt(0).toUpperCase() + selectedIdea.type.slice(1)} Idea
              </span>
              <span className="brain-dump__detail-time">
                {formatRelativeTime(selectedIdea.createdAt)}
              </span>
            </div>

            <p className="brain-dump__detail-content">{selectedIdea.content}</p>

            {selectedIdea.type === 'link' && selectedIdea.linkUrl && (
              <a
                href={selectedIdea.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="brain-dump__detail-link"
              >
                <Link2 size={16} />
                {selectedIdea.linkTitle || selectedIdea.linkUrl}
                <ChevronRight size={16} />
              </a>
            )}

            {selectedIdea.tags.length > 0 && (
              <div className="brain-dump__detail-tags">
                {selectedIdea.tags.map(tag => {
                  const tagInfo = systemTags.find(t => t.name === tag);
                  return (
                    <span
                      key={tag}
                      className={`brain-dump__idea-tag brain-dump__idea-tag--${tagInfo?.color || 'gray'}`}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="brain-dump__detail-actions">
              <Button
                variant="ghost"
                onClick={() => handleArchiveIdea(selectedIdea.id)}
              >
                <Archive size={18} />
                Archive
              </Button>
              <Button
                variant="ghost"
                className="brain-dump__detail-delete"
                onClick={() => handleDeleteIdea(selectedIdea.id)}
              >
                <Trash2 size={18} />
                Delete
              </Button>
              <Button>
                <Send size={18} />
                Promote to Work
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
    </AppLayout>
  );
};

export default BrainDump;
